import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSupervisorFellows, getMeetings, getAnnouncements } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon, AcademicCapIcon, CalendarDaysIcon,
  MegaphoneIcon, ClockIcon, CheckBadgeIcon,
} from '@heroicons/react/24/outline';

function StatusBadge({ status }) {
  const map = {
    Active: 'bg-green-900/50 text-green-300',
    Completed: 'bg-blue-900/50 text-blue-300',
    'On Leave': 'bg-amber-900/50 text-amber-300',
  };
  return <span className={`badge ${map[status] || 'bg-gray-800 text-gray-400'}`}>{status}</span>;
}

export default function SupervisorPage() {
  const { user } = useAuth();

  const { data: fellowsData, isLoading } = useQuery({
    queryKey: ['supervisor-fellows'],
    queryFn: getSupervisorFellows,
  });

  const { data: meetingsData } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => getMeetings({ upcoming: true }),
  });

  const { data: announcementsData } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => getAnnouncements({ limit: 5 }),
  });

  const fellows = fellowsData?.data || [];
  const meetings = (meetingsData?.data || []).slice(0, 3);
  const announcements = (announcementsData?.data || []).slice(0, 4);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{greeting}, {user?.full_name?.split(' ')[0]} 👋</h1>
        <p className="text-gray-500 text-sm mt-1">
          Supervisor Portal — THRU Zim MSc Fellowship
        </p>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0">
            <UserCircleIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{fellows.length}</div>
            <div className="text-gray-500 text-sm">Your Fellows</div>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
            <CheckBadgeIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">
              {fellows.filter(f => f.current_status === 'Active').length}
            </div>
            <div className="text-gray-500 text-sm">Active</div>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-purple-600 flex items-center justify-center flex-shrink-0">
            <CalendarDaysIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{meetings.length}</div>
            <div className="text-gray-500 text-sm">Upcoming Meetings</div>
          </div>
        </div>
      </div>

      {/* Fellows */}
      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <AcademicCapIcon className="w-4 h-4" /> Your Assigned Fellows
        </h2>
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : fellows.length === 0 ? (
          <div className="card text-center py-10">
            <UserCircleIcon className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-400">No fellows assigned to you yet.</p>
            <p className="text-gray-600 text-sm mt-1">Contact the admin to be linked to your fellows.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fellows.map((p) => (
              <Link key={p.id} to={`/fellows/${p.user_id}`} className="card hover:border-gray-600 transition-all block">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-600/30 flex items-center justify-center text-primary-400 font-bold text-sm flex-shrink-0">
                    {p.user?.full_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate">{p.user?.full_name}</div>
                    <div className="text-xs text-gray-500 truncate">{p.user?.email}</div>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs text-gray-400">
                  {p.degree && (
                    <div className="flex items-center gap-1.5">
                      <AcademicCapIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{p.degree}</span>
                    </div>
                  )}
                  {p.university && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-600">@</span>
                      <span className="truncate">{p.university}</span>
                    </div>
                  )}
                  {p.project_title && (
                    <div className="text-gray-500 line-clamp-2 mt-1">{p.project_title}</div>
                  )}
                  {p.expected_completion && (
                    <div className="flex items-center gap-1.5 text-amber-400/80">
                      <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Due {format(new Date(p.expected_completion), 'MMM yyyy')}</span>
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <StatusBadge status={p.current_status || 'Active'} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Meetings */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <CalendarDaysIcon className="w-4 h-4 text-green-400" /> Upcoming Meetings
            </h2>
          </div>
          {meetings.length === 0 ? (
            <p className="text-gray-500 text-sm">No upcoming meetings.</p>
          ) : (
            <div className="space-y-3">
              {meetings.map((m) => (
                <div key={m.id} className="bg-surface-100 rounded-lg px-4 py-3">
                  <div className="text-sm font-semibold text-white">{m.title}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {format(new Date(m.meeting_date), 'EEEE, dd MMM yyyy')} at {format(new Date(m.meeting_date), 'HH:mm')}
                    <span className="text-primary-400 ml-2">({formatDistanceToNow(new Date(m.meeting_date), { addSuffix: true })})</span>
                  </div>
                  {m.zoom_link && (
                    <a href={m.zoom_link} target="_blank" rel="noreferrer" className="text-xs text-primary-400 hover:underline mt-1 inline-block">
                      Join Zoom
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Announcements */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <MegaphoneIcon className="w-4 h-4 text-primary-400" /> Latest Announcements
            </h2>
          </div>
          {announcements.length === 0 ? (
            <p className="text-gray-500 text-sm">No announcements yet.</p>
          ) : (
            <div className="space-y-3">
              {announcements.map((ann) => (
                <div key={ann.id} className="border-l-2 border-primary-600 pl-3">
                  <div className="text-sm font-medium text-gray-200">{ann.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {ann.author?.full_name} · {format(new Date(ann.created_at), 'dd MMM yyyy')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
